import { Capacitor } from '@capacitor/core';
import { SocialLogin } from '@capgo/capacitor-social-login';
import { saveAppleSignInProfile } from './appleSignInStorage';

export interface AppleSignInResult {
  idToken: string;
  email?: string;
  givenName?: string;
  familyName?: string;
  fullName?: string;
}

function pickString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
}

function decodeJwtPayload(idToken: string): Record<string, unknown> | null {
  try {
    const parts = idToken.split('.');
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const json = atob(padded);
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function buildFullName(givenName?: string, familyName?: string): string | undefined {
  const fullName = [givenName, familyName].filter(Boolean).join(' ').trim();
  return fullName || undefined;
}

function extractProfileNames(profile: Record<string, unknown> | null | undefined): {
  givenName?: string;
  familyName?: string;
  displayName?: string;
} {
  if (!profile) return {};

  const givenName =
    pickString(profile.givenName) ??
    pickString(profile.given_name) ??
    pickString(profile.firstName) ??
    pickString(profile.first_name);

  const familyName =
    pickString(profile.familyName) ??
    pickString(profile.family_name) ??
    pickString(profile.lastName) ??
    pickString(profile.last_name);

  const displayName =
    pickString(profile.displayName) ??
    pickString(profile.display_name) ??
    pickString(profile.fullName) ??
    pickString(profile.full_name) ??
    pickString(profile.name) ??
    pickString(profile.nickname);

  return { givenName, familyName, displayName };
}

function resolveFullName(
  displayName?: string,
  givenName?: string,
  familyName?: string,
): string | undefined {
  return displayName ?? buildFullName(givenName, familyName);
}

export class AppleAuthCore {
  private static isInitialized = false;

  static async initialize(): Promise<void> {
    if (this.isInitialized) return;

    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') {
      throw new Error('AppleAuthCore.initialize is only supported on native iOS.');
    }

    await SocialLogin.initialize({
      apple: {},
    });

    this.isInitialized = true;
  }

  static async signIn(): Promise<AppleSignInResult> {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') {
      throw new Error('Apple sign-in is only supported on native iOS.');
    }

    await this.initialize();

    const result = await SocialLogin.login({
      provider: 'apple',
      options: {},
    });

    console.log('[AppleSignIn] SocialLogin.login provider=', result?.provider);

    if (!result?.result || !('idToken' in result.result) || !result.result.idToken) {
      console.warn('[AppleSignIn] No idToken in login result', result);
      throw new Error('No token received from Apple sign-in');
    }

    const idToken = result.result.idToken;
    const profileRaw = result.result.profile as Record<string, unknown> | undefined;
    const jwtPayload = decodeJwtPayload(idToken);

    console.log('[AppleSignIn] profile from plugin:', JSON.stringify(profileRaw ?? null));
    console.log('[AppleSignIn] JWT email claim:', pickString(jwtPayload?.email) ?? '(none)');

    const { givenName: profileGiven, familyName: profileFamily, displayName: profileDisplay } =
      extractProfileNames(profileRaw);
    const givenName = profileGiven;
    const familyName = profileFamily;
    const profileEmail = pickString(profileRaw?.email);
    const jwtEmail = pickString(jwtPayload?.email);
    const email = profileEmail || jwtEmail;
    const fullName = resolveFullName(profileDisplay, givenName, familyName);

    console.log('[AppleSignIn] parsed:', {
      givenName: givenName ?? '(empty)',
      familyName: familyName ?? '(empty)',
      displayName: profileDisplay ?? '(empty)',
      fullName: fullName ?? '(empty)',
      email: email ?? '(empty)',
    });

    const appleResult: AppleSignInResult = {
      idToken,
      email,
      givenName,
      familyName,
      fullName,
    };

    await saveAppleSignInProfile(appleResult);

    return appleResult;
  }
}
